import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import {
  Search, Building2, MapPin, Star, AlertCircle, CheckCircle,
  X, ChevronRight, ChevronDown, Link2, Unlink, ArrowRight, Shield, Loader2,
  Filter, Globe, BadgeCheck, Pencil
} from "lucide-react";
import styles from "./HotelMappingTab.module.css";
import SearchableSelect from "../../../BaseComponents/SearchableSelect/SearchableSelect";
import {
  useHotelMappings,
  useCreateHotelMapping,
  useDeleteHotelMapping,
  useVerifyHotelMapping,
  useUpdateHotelMapping,
  useMappingStats,
  mapApiMappingToUi,
  mapApiMasterHotelToUi,
  fetchAndMatchSupplierHotels,
  mapApiSupplierSuggestionToUi,
  getMappedHotels,
  getUnmappedHotels,
  getApiSuppliers,
} from "../../../../api/products/useHotelMappingApi";
import { useGetHotels, getHotelCountries, getHotelCities, getHotelZones } from "../../../../api/products/useHotelsApi";
import axiosInstance from "../../../../api/axiosInstance";
import { useToast } from "../../../../context/ToastContext";

const LIST_PAGE_SIZE = 20;

const HotelMappingTab = () => {
  const { showToast } = useToast();

  // ── View: 'map' (3-panel mapper) | 'mapped' | 'unmapped' ──────────────────
  const [view, setView] = useState("map");

  // ── Geo + supplier filters (shared across all views) ──────────────────────
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [zones, setZones] = useState([]);
  const [countryFilter, setCountryFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [zoneFilter, setZoneFilter] = useState("");
  const [apiSuppliers, setApiSuppliers] = useState([]);
  const [supplierFilter, setSupplierFilter] = useState("all");

  // ── Mapper state ──────────────────────────────────────────────────────────
  const [selectedHotelbeds, setSelectedHotelbeds] = useState(null);
  const [supplierSuggestions, setSupplierSuggestions] = useState([]);
  const [supplierErrors, setSupplierErrors] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [supplierPage, setSupplierPage] = useState(1);
  const SUPPLIERS_PER_PAGE = 20;
  const [hotelbedsSearch, setHotelbedsSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [supplierSearch, setSupplierSearch] = useState("");
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [message, setMessage] = useState(null);
  const [hotelPage, setHotelPage] = useState(1);
  const [allHotels, setAllHotels] = useState([]);
  const [loadingMoreHotels, setLoadingMoreHotels] = useState(false);
  const [hasMoreHotels, setHasMoreHotels] = useState(true);
  const [apiPagination, setApiPagination] = useState(null);
  const mappingCenterRef = useRef(null);
  // Monotonic tokens so only the newest in-flight request may write state.
  // hotelQueryRef bumps whenever the hotel query (search or geo filters)
  // changes; listRequestRef bumps per mapped/unmapped list request.
  const hotelQueryRef = useRef(0);
  const listRequestRef = useRef(0);

  // ── Mapped / Unmapped list-view state ─────────────────────────────────────
  const [listRows, setListRows] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listPage, setListPage] = useState(1);
  const [listPagination, setListPagination] = useState(null);
  const [listSearch, setListSearch] = useState("");
  const [debouncedListSearch, setDebouncedListSearch] = useState("");
  const [expandedHotelId, setExpandedHotelId] = useState(null);
  const [editingMappingId, setEditingMappingId] = useState(null);

  // ── Load filter options ───────────────────────────────────────────────────
  useEffect(() => {
    getHotelCountries()
      .then((res) => setCountries(res?.data || []))
      .catch(() => setCountries([]));
    getApiSuppliers()
      .then((res) => setApiSuppliers(res?.data || []))
      .catch(() => setApiSuppliers([]));
  }, []);

  useEffect(() => {
    if (!countryFilter) { setCities([]); return; }
    getHotelCities({ country: countryFilter })
      .then((res) => setCities(res?.data || []))
      .catch(() => setCities([]));
  }, [countryFilter]);

  useEffect(() => {
    if (!countryFilter || !cityFilter) { setZones([]); return; }
    getHotelZones({ country: countryFilter, city: cityFilter })
      .then((res) => setZones(res?.data || []))
      .catch(() => setZones([]));
  }, [countryFilter, cityFilter]);

  // Cascading resets — child filters clear when the parent changes.
  const handleCountryChange = (value) => {
    setCountryFilter(value);
    setCityFilter("");
    setZoneFilter("");
  };
  const handleCityChange = (value) => {
    setCityFilter(value);
    setZoneFilter("");
  };
  const clearFilters = () => {
    setCountryFilter("");
    setCityFilter("");
    setZoneFilter("");
    setSupplierFilter("all");
  };

  // ── Debounce master-hotel search; reset the accumulated list on any change
  //    of search or geo filters ───────────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      hotelQueryRef.current += 1; // invalidate any in-flight "load more"
      setDebouncedSearch(hotelbedsSearch);
      setHotelPage(1);
      setAllHotels([]);
      setHasMoreHotels(true);
    }, 400);
    return () => clearTimeout(timer);
  }, [hotelbedsSearch]);

  useEffect(() => {
    // Geo filters changed — restart the hotel list from page 1 and invalidate
    // any pending page-N request issued under the previous filters.
    hotelQueryRef.current += 1;
    setHotelPage(1);
    setAllHotels([]);
    setHasMoreHotels(true);
  }, [countryFilter, cityFilter, zoneFilter]);

  // Master Hotels from API — re-fetches when search or geo filters change
  const { data: masterHotels, loading: hotelsLoading, error: hotelsError } = useGetHotels({
    limit: 100,
    search: debouncedSearch,
    country: countryFilter || "all",
    city: cityFilter || "all",
    zone: zoneFilter === "" ? "all" : zoneFilter,
    sortBy: "name",
    sortOrder: "ASC",
  });

  useEffect(() => {
    if (!masterHotels || masterHotels.length === 0) return;
    if (hotelPage === 1) {
      setAllHotels(masterHotels);
      return;
    }
    setAllHotels((prev) => {
      const existingIds = new Set(prev.map((h) => h.id));
      const uniqueNew = masterHotels.filter((h) => !existingIds.has(h.id));
      return [...prev, ...uniqueNew];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterHotels]);

  // Hotel Mappings for selected hotel
  const selectedHotelbedsId = selectedHotelbeds?.id;
  const { data: mappingData, loading: mappingsLoading, refetch: refetchMappings } = useHotelMappings(selectedHotelbedsId);

  // Supplier ids the fetch should cover — the dropdown selection, or every
  // credentialed supplier for "All suppliers".
  const activeSupplierIds = useMemo(() => {
    if (supplierFilter === "all") return apiSuppliers.map((s) => s.id);
    return [parseInt(supplierFilter)];
  }, [supplierFilter, apiSuppliers]);

  // ── Fetch supplier suggestions when hotel is selected (or supplier changes)
  const fetchSuggestions = useCallback(async (signal) => {
    if (!selectedHotelbedsId || activeSupplierIds.length === 0) {
      setSupplierSuggestions([]);
      setSupplierErrors([]);
      return;
    }
    setLoadingSuggestions(true);
    try {
      const payload = {
        masterHotelId: selectedHotelbedsId,
        supplierIds: activeSupplierIds,
        filters: {},
      };
      const response = await fetchAndMatchSupplierHotels(payload, signal);
      if (response.success && response.data) {
        setSupplierSuggestions((response.data.suggestions || []).map(mapApiSupplierSuggestionToUi));
        setSupplierErrors(response.data.supplierErrors || []);
        setSupplierPage(1);
      } else {
        setSupplierSuggestions([]);
        setSupplierErrors([]);
      }
    } catch (error) {
      if (error.name !== "CanceledError" && error.name !== "AbortError") {
        console.error("Error fetching supplier suggestions:", error);
        setSupplierSuggestions([]);
        setSupplierErrors([{ supplierName: "Request", error: error.message }]);
      }
    } finally {
      if (!signal || !signal.aborted) setLoadingSuggestions(false);
    }
  }, [selectedHotelbedsId, activeSupplierIds]);

  useEffect(() => {
    const controller = new AbortController();
    fetchSuggestions(controller.signal);
    return () => controller.abort();
  }, [fetchSuggestions]);

  // Create/Delete/Verify/Update mapping operations
  const { createMapping, loading: createLoading } = useCreateHotelMapping();
  const { deleteMapping, loading: deleteLoading } = useDeleteHotelMapping();
  const { verifyMapping, loading: verifyLoading } = useVerifyHotelMapping();
  const { updateMapping, loading: updateLoading } = useUpdateHotelMapping();

  // Global stats
  const { data: statsData, refetch: refetchStats } = useMappingStats();

  const filteredHotelbeds = useMemo(() => allHotels.map(mapApiMasterHotelToUi), [allHotels]);

  const mappedSuppliers = useMemo(() => {
    if (!selectedHotelbeds || !mappingData) return [];
    return (mappingData.mappings || []).map(mapApiMappingToUi);
  }, [selectedHotelbeds, mappingData]);

  const availableSuppliers = useMemo(() => {
    if (!selectedHotelbeds) return [];
    const all = [...supplierSuggestions].sort((a, b) => {
      // Already-mapped-to-this-hotel entries sink to the bottom; rest by score.
      if (a.mappedToThisHotel !== b.mappedToThisHotel) return a.mappedToThisHotel ? 1 : -1;
      return (b.matchScore || 0) - (a.matchScore || 0);
    });
    if (!supplierSearch) return all;
    const q = supplierSearch.toLowerCase();
    return all.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        String(s.supplierCode).toLowerCase().includes(q) ||
        (s.supplier || "").toLowerCase().includes(q)
    );
  }, [supplierSuggestions, supplierSearch, selectedHotelbeds]);

  const paginatedSuppliers = useMemo(() => {
    const startIndex = (supplierPage - 1) * SUPPLIERS_PER_PAGE;
    return availableSuppliers.slice(startIndex, startIndex + SUPPLIERS_PER_PAGE);
  }, [availableSuppliers, supplierPage]);

  const totalSupplierPages = Math.ceil(availableSuppliers.length / SUPPLIERS_PER_PAGE);

  const stats = useMemo(() => ({
    totalHotels: apiPagination?.total || statsData.totalHotels || allHotels.length,
    mappedHotels: statsData.totalMappedHotels || 0,
    unmappedHotels: statsData.unmappedHotels || 0,
    currentHotelMappings: selectedHotelbeds ? mappedSuppliers.length : 0,
    loadedHotels: allHotels.length,
  }), [apiPagination, allHotels, selectedHotelbeds, mappedSuppliers, statsData]);

  const showMessage = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 4000);
  };

  const handleSelectHotelbeds = (hotel) => {
    setSelectedHotelbeds(hotel);
    setSelectedSuppliers([]);
    setSupplierSearch("");
    // Bring the mapping panel into view — with a long hotel list the panel top
    // can be far above the current scroll position.
    setTimeout(() => {
      mappingCenterRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 50);
  };

  const handleToggleSupplier = (supplier) => {
    if (supplier.mappedToThisHotel) return; // already mapped — nothing to do
    setSelectedSuppliers((prev) => {
      const exists = prev.find((s) => s.id === supplier.id);
      return exists ? prev.filter((s) => s.id !== supplier.id) : [...prev, supplier];
    });
  };

  const handleMapSelected = async () => {
    if (!selectedHotelbeds || selectedSuppliers.length === 0) {
      showToast("Please select supplier hotels to map", "error");
      return;
    }
    try {
      for (const supplier of selectedSuppliers) {
        const result = await createMapping({
          masterHotelId: selectedHotelbeds.id,
          supplierId: supplier.supplierId,
          supplierHotelCode: supplier.supplierCode,
          supplierHotelName: supplier.name,
          matchScore: supplier.matchScore || 0,
          matchMethod: "Manual",
          mappingStatus: "Verified",
          priority: 0,
          city: supplier.city,
          countryCode: supplier.country,
          latitude: supplier.latitude,
          longitude: supplier.longitude,
        });
        if (!result.success) throw new Error(result.error);
      }

      // Mark the mapped suggestions locally so they show as done immediately.
      const mappedIds = new Set(selectedSuppliers.map((s) => s.id));
      setSupplierSuggestions((prev) =>
        prev.map((s) =>
          mappedIds.has(s.id)
            ? { ...s, mappedToThisHotel: true, mappedToHotelbedsId: selectedHotelbeds.id }
            : s
        )
      );

      refetchMappings();
      refetchStats();
      showToast(`Mapped ${selectedSuppliers.length} supplier hotel(s) to ${selectedHotelbeds.name}`, "success");
      showMessage(`✓ Mapped ${selectedSuppliers.length} supplier(s) to ${selectedHotelbeds.name}`, "success");
      setSelectedSuppliers([]);
    } catch (error) {
      showToast(`Failed to map suppliers: ${error.message}`, "error");
      showMessage(`Failed to map suppliers: ${error.message}`, "error");
    }
  };

  const handleUnmap = async (mappingId, label) => {
    try {
      // Identify the exact supplier hotel being unmapped BEFORE deleting, so
      // only its suggestion is re-enabled. Clearing by master-hotel id would
      // also un-flag a sibling supplier's still-live mapping, letting the user
      // re-map it into a duplicate-key error.
      const unmapped = mappedSuppliers.find((m) => m.id === mappingId);
      const result = await deleteMapping(mappingId);
      if (!result.success) throw new Error(result.error);
      refetchMappings();
      refetchStats();
      setSupplierSuggestions((prev) =>
        prev.map((s) => {
          const isSame = unmapped
            ? String(s.supplierId) === String(unmapped.supplierId) &&
              String(s.supplierCode) === String(unmapped.supplierHotelCode)
            : s.mappedMappingId === mappingId;
          return isSame
            ? { ...s, mappedToThisHotel: false, mappedToHotelbedsId: null, mappedMappingId: null }
            : s;
        })
      );
      showToast(`Unmapped ${label || "supplier hotel"}`, "success");
      showMessage(`✓ Unmapped ${label || "supplier hotel"}`, "success");
    } catch (error) {
      showToast(`Failed to unmap: ${error.message}`, "error");
      showMessage(`Failed to unmap: ${error.message}`, "error");
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 85) return "#10b981";
    if (confidence >= 70) return "#f59e0b";
    return "#ef4444";
  };

  const loadMoreHotels = async () => {
    if (!hasMoreHotels || loadingMoreHotels) return;
    // Snapshot the query generation: if the user changes search/geo filters
    // while this page is in flight, its rows belong to the old filter set and
    // must be discarded rather than appended onto the freshly reset list.
    const queryToken = hotelQueryRef.current;
    setLoadingMoreHotels(true);
    try {
      const nextPage = hotelPage + 1;
      const params = new URLSearchParams({ page: nextPage, limit: 100, sortBy: "name", sortOrder: "ASC" });
      if (debouncedSearch) params.set("search", debouncedSearch);
      if (countryFilter) params.set("country", countryFilter);
      if (cityFilter) params.set("city", cityFilter);
      if (zoneFilter !== "") params.set("zone", zoneFilter);

      const response = await axiosInstance.get(`/hotels?${params.toString()}`);
      if (queryToken !== hotelQueryRef.current) return; // filters changed — drop
      const data = response.data;
      if (data.success && data.data) {
        const newHotels = data.data;
        setAllHotels((prev) => {
          const existingIds = new Set(prev.map((hotel) => hotel.id));
          return [...prev, ...newHotels.filter((hotel) => !existingIds.has(hotel.id))];
        });
        setHotelPage(nextPage);
        if (data.pagination) {
          setApiPagination(data.pagination);
          setHasMoreHotels(nextPage < data.pagination.totalPages);
        } else {
          setHasMoreHotels(newHotels.length === 100);
        }
      } else {
        setHasMoreHotels(false);
      }
    } catch (error) {
      console.error("Error loading more hotels:", error);
      if (queryToken === hotelQueryRef.current) setHasMoreHotels(false);
    } finally {
      setLoadingMoreHotels(false);
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 100) loadMoreHotels();
  };

  // ── Mapped / Unmapped list views ──────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedListSearch(listSearch);
      setListPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [listSearch]);

  // A filter change must restart the list at page 1 — otherwise a narrower
  // filter is requested at the old page number and returns an empty page that
  // reads as "no results" (pager showing e.g. 5 / 2).
  useEffect(() => {
    setListPage(1);
  }, [countryFilter, cityFilter, zoneFilter, supplierFilter]);

  const loadListView = useCallback(async () => {
    if (view === "map") return;
    // Only the newest request may write state: the mapped query is far heavier
    // than the unmapped one, so a slow earlier response can land last and
    // overwrite the view the user is actually looking at.
    const requestId = ++listRequestRef.current;
    setListLoading(true);
    try {
      const params = {
        page: listPage,
        limit: LIST_PAGE_SIZE,
      };
      if (debouncedListSearch) params.search = debouncedListSearch;
      if (countryFilter) params.countryCode = countryFilter;
      if (cityFilter) params.destinationCode = cityFilter;
      if (zoneFilter !== "") params.zoneCode = zoneFilter;
      if (view === "mapped" && supplierFilter !== "all") params.supplierId = supplierFilter;

      const res = view === "mapped" ? await getMappedHotels(params) : await getUnmappedHotels(params);
      if (requestId !== listRequestRef.current) return; // superseded
      if (res.success) {
        setListRows(res.data || []);
        setListPagination(res.pagination || null);
      } else {
        setListRows([]);
        setListPagination(null);
        showToast(res.message || "Failed to load hotels", "error");
      }
    } catch (error) {
      if (requestId !== listRequestRef.current) return; // superseded
      setListRows([]);
      setListPagination(null);
      showToast(error.response?.data?.message || error.message || "Failed to load hotels", "error");
    } finally {
      if (requestId === listRequestRef.current) setListLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, listPage, debouncedListSearch, countryFilter, cityFilter, zoneFilter, supplierFilter]);

  useEffect(() => {
    loadListView();
  }, [loadListView]);

  const switchView = (nextView) => {
    setView(nextView);
    setListPage(1);
    setListRows([]);
    setListPagination(null);
    setExpandedHotelId(null);
    setEditingMappingId(null);
  };

  // Jump from a list view into the mapper with the hotel pre-selected.
  const openInMapper = (row) => {
    setView("map");
    handleSelectHotelbeds({
      id: row.id,
      hotelCode: row.hotelCode,
      name: row.displayName || row.name,
      city: row.city || "",
      country: row.countryCode || "",
      stars: 0,
      zone: row.zoneName || "",
    });
  };

  // Mapped view: verify / status change / unmap actions
  const handleVerify = async (mappingId) => {
    const result = await verifyMapping(mappingId);
    if (result.success) {
      showToast("Mapping verified", "success");
      loadListView();
      refetchStats();
    } else {
      showToast(result.error || "Failed to verify mapping", "error");
    }
  };

  const handleStatusChange = async (mappingId, mappingStatus) => {
    const result = await updateMapping(mappingId, { mappingStatus });
    if (result.success) {
      showToast(`Mapping status set to ${mappingStatus}`, "success");
      setEditingMappingId(null);
      loadListView();
      refetchStats();
    } else {
      showToast(result.error || "Failed to update mapping", "error");
    }
  };

  const handleListUnmap = async (mappingId, label) => {
    const result = await deleteMapping(mappingId);
    if (result.success) {
      showToast(`Unmapped ${label || "supplier hotel"}`, "success");
      loadListView();
      refetchStats();
    } else {
      showToast(result.error || "Failed to unmap", "error");
    }
  };

  const statusBadgeClass = (status) => {
    if (status === "Verified") return styles.statusVerified;
    if (status === "Rejected") return styles.statusRejected;
    if (status === "Auto-Matched") return styles.statusAuto;
    return styles.statusPending;
  };

  const supplierNameOf = (id) => apiSuppliers.find((s) => String(s.id) === String(id))?.name;

  return (
    <div className={styles.container}>
      {/* Message Banner */}
      {message && (
        <div className={`${styles.banner} ${message.type === "error" ? styles.bannerError : styles.bannerSuccess}`}>
          {message.type === "error" ? <AlertCircle size={18} /> : <CheckCircle size={18} />}
          <span className={styles.bannerText}>{message.text}</span>
          <X size={16} className={styles.closeBtn} onClick={() => setMessage(null)} />
        </div>
      )}

      {/* Stats — Mapped / Unmapped tiles switch to their management views */}
      <div className={styles.statsGrid}>
        <div
          className={`${styles.statCard} ${styles.statClickable} ${view === "map" ? styles.statActive : ""}`}
          onClick={() => switchView("map")}
          title="Open the mapping workspace"
        >
          <div className={styles.statIcon} style={{ background: '#fff7ed', color: '#f59e0b' }}>
            <Building2 size={20} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{stats.totalHotels}</div>
            <div className={styles.statLabel}>Master Hotels</div>
          </div>
        </div>
        <div
          className={`${styles.statCard} ${styles.statClickable} ${view === "mapped" ? styles.statActive : ""}`}
          onClick={() => switchView("mapped")}
          title="View and edit mapped hotels"
        >
          <div className={styles.statIcon} style={{ background: '#ecfdf5', color: '#10b981' }}>
            <CheckCircle size={20} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{stats.mappedHotels}</div>
            <div className={styles.statLabel}>Mapped Hotels</div>
          </div>
          <ChevronRight size={16} className={styles.statArrow} />
        </div>
        <div
          className={`${styles.statCard} ${styles.statClickable} ${view === "unmapped" ? styles.statActive : ""}`}
          onClick={() => switchView("unmapped")}
          title="View unmapped hotels and map them"
        >
          <div className={styles.statIcon} style={{ background: '#fef2f2', color: '#ef4444' }}>
            <AlertCircle size={20} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{stats.unmappedHotels}</div>
            <div className={styles.statLabel}>Unmapped Hotels</div>
          </div>
          <ChevronRight size={16} className={styles.statArrow} />
        </div>
        {view === "map" && selectedHotelbeds && (
          <div className={styles.statCard} style={{ borderColor: '#f59e0b', borderWidth: '2px' }}>
            <div className={styles.statIcon} style={{ background: '#fff7ed', color: '#f59e0b' }}>
              <Link2 size={20} />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>{stats.currentHotelMappings}</div>
              <div className={styles.statLabel}>This Hotel's Mappings</div>
            </div>
          </div>
        )}
      </div>

      {/* Filter bar — geo cascade + supplier */}
      <div className={styles.filterBar}>
        <div className={styles.filterIcon}><Filter size={15} /></div>
        <SearchableSelect
          width={200}
          placeholder="All Countries"
          searchPlaceholder="Search country..."
          value={countryFilter}
          options={[
            { label: "All Countries", value: "" },
            ...countries.map((c) => ({ label: c.countryName || c.countryCode, value: c.countryCode })),
          ]}
          onChange={handleCountryChange}
        />
        <SearchableSelect
          width={200}
          placeholder="Destination (pick country)"
          searchPlaceholder="Search destination..."
          value={cityFilter}
          disabled={!countryFilter}
          options={[
            { label: countryFilter ? "All Destinations" : "Destination (pick country)", value: "" },
            ...cities.map((c) => ({ label: c.cityName || c.cityCode, value: c.cityCode })),
          ]}
          onChange={handleCityChange}
        />
        <SearchableSelect
          width={200}
          placeholder="Zone (pick destination)"
          searchPlaceholder="Search zone..."
          value={zoneFilter}
          disabled={!cityFilter}
          options={[
            { label: cityFilter ? "All Zones" : "Zone (pick destination)", value: "" },
            ...zones.map((z) => ({ label: z.zoneName || `Zone ${z.zoneCode}`, value: z.zoneCode })),
          ]}
          onChange={setZoneFilter}
        />
        <SearchableSelect
          width={200}
          placeholder="All Suppliers"
          searchPlaceholder="Search supplier..."
          value={supplierFilter}
          options={[
            { label: "All Suppliers", value: "all" },
            ...apiSuppliers.map((sup) => ({ label: sup.name, value: String(sup.id) })),
          ]}
          onChange={setSupplierFilter}
        />
        {(countryFilter || cityFilter || zoneFilter !== "" || supplierFilter !== "all") && (
          <button className={styles.clearFilters} onClick={clearFilters}>
            <X size={13} /> Clear
          </button>
        )}
      </div>

      {/* ═══════════ MAPPED HOTELS VIEW ═══════════ */}
      {view === "mapped" && (
        <div className={styles.listView}>
          <div className={styles.listHeader}>
            <div className={styles.listTitle}>
              <CheckCircle size={18} color="#10b981" />
              <span>Mapped Hotels</span>
              {listPagination && <span className={styles.listTotal}>{listPagination.total} hotels</span>}
            </div>
            <div className={styles.searchBox}>
              <Search size={16} />
              <input
                type="text"
                placeholder="Search mapped hotels..."
                value={listSearch}
                onChange={(e) => setListSearch(e.target.value)}
                className={styles.searchInput}
              />
            </div>
          </div>

          {listLoading ? (
            <div className={styles.loadingState}><Loader2 size={24} className={styles.spinner} /><p>Loading mapped hotels...</p></div>
          ) : listRows.length === 0 ? (
            <div className={styles.emptyState}><CheckCircle size={40} color="#cbd5e1" /><p>No mapped hotels found for these filters</p></div>
          ) : (
            <div className={styles.listRows}>
              {listRows.map((row) => (
                <div key={row.id} className={styles.listRowCard}>
                  <div className={styles.listRowMain} onClick={() => setExpandedHotelId(expandedHotelId === row.id ? null : row.id)}>
                    {expandedHotelId === row.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    <div className={styles.listRowInfo}>
                      <div className={styles.listRowName}>{row.displayName || row.name}</div>
                      <div className={styles.listRowMeta}>
                        <MapPin size={11} />
                        <span>{row.city || "—"}{row.countryCode ? `, ${row.countryCode}` : ""}</span>
                        {row.zoneName && <span className={styles.zoneTag}>{row.zoneName}</span>}
                        <span className={styles.codeTag}>#{row.hotelCode}</span>
                      </div>
                    </div>
                    <div className={styles.mappingCountBadge}>
                      <Link2 size={12} /> {row.mappingCount} mapping{row.mappingCount > 1 ? "s" : ""}
                    </div>
                    <button
                      className={styles.openMapperBtn}
                      onClick={(e) => { e.stopPropagation(); openInMapper(row); }}
                      title="Open this hotel in the mapper"
                    >
                      <ArrowRight size={13} /> Open in Mapper
                    </button>
                  </div>

                  {expandedHotelId === row.id && (
                    <div className={styles.mappingDetailList}>
                      {(row.mappings || []).map((m) => (
                        <div key={m.id} className={styles.mappingDetailRow}>
                          <div className={styles.mappingDetailInfo}>
                            <span className={styles.supplierBadge}>{m.supplierFullName || m.supplierName || supplierNameOf(m.supplierId) || `Supplier ${m.supplierId}`}</span>
                            <span className={styles.mappingHotelName}>{m.supplierHotelName || m.supplierHotelCode}</span>
                            <span className={styles.codeTag}>{m.supplierHotelCode}</span>
                            {m.matchScore != null && (
                              <span className={styles.scoreTag} style={{ color: getConfidenceColor(m.matchScore) }}>
                                {Math.round(m.matchScore)}%
                              </span>
                            )}
                          </div>
                          <div className={styles.mappingDetailActions}>
                            {editingMappingId === m.id ? (
                              <select
                                className={styles.statusSelect}
                                value={m.mappingStatus}
                                onChange={(e) => handleStatusChange(m.id, e.target.value)}
                                disabled={updateLoading}
                                autoFocus
                              >
                                <option value="Pending">Pending</option>
                                <option value="Verified">Verified</option>
                                <option value="Auto-Matched">Auto-Matched</option>
                                <option value="Rejected">Rejected</option>
                              </select>
                            ) : (
                              <span className={`${styles.statusBadge} ${statusBadgeClass(m.mappingStatus)}`}>{m.mappingStatus}</span>
                            )}
                            <button
                              className={styles.iconBtn}
                              onClick={() => setEditingMappingId(editingMappingId === m.id ? null : m.id)}
                              title="Edit mapping status"
                            >
                              <Pencil size={13} />
                            </button>
                            {m.mappingStatus !== "Verified" && (
                              <button
                                className={styles.verifyBtn}
                                onClick={() => handleVerify(m.id)}
                                disabled={verifyLoading}
                                title="Mark as verified"
                              >
                                <BadgeCheck size={13} /> Verify
                              </button>
                            )}
                            <button
                              className={styles.unmapButton}
                              onClick={() => handleListUnmap(m.id, m.supplierHotelName)}
                              disabled={deleteLoading}
                              title="Remove this mapping"
                            >
                              <Unlink size={13} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {listPagination && listPagination.totalPages > 1 && (
            <div className={styles.pagination}>
              <button className={styles.prevButton} onClick={() => setListPage(listPage - 1)} disabled={listPage === 1}>Prev</button>
              <span className={styles.pageNumber}>{listPage} / {listPagination.totalPages}</span>
              <button className={styles.nextButton} onClick={() => setListPage(listPage + 1)} disabled={listPage >= listPagination.totalPages}>Next</button>
            </div>
          )}
        </div>
      )}

      {/* ═══════════ UNMAPPED HOTELS VIEW ═══════════ */}
      {view === "unmapped" && (
        <div className={styles.listView}>
          <div className={styles.listHeader}>
            <div className={styles.listTitle}>
              <AlertCircle size={18} color="#ef4444" />
              <span>Unmapped Hotels</span>
              {listPagination && <span className={styles.listTotal}>{listPagination.total} hotels</span>}
            </div>
            <div className={styles.searchBox}>
              <Search size={16} />
              <input
                type="text"
                placeholder="Search unmapped hotels..."
                value={listSearch}
                onChange={(e) => setListSearch(e.target.value)}
                className={styles.searchInput}
              />
            </div>
          </div>

          {listLoading ? (
            <div className={styles.loadingState}><Loader2 size={24} className={styles.spinner} /><p>Loading unmapped hotels...</p></div>
          ) : listRows.length === 0 ? (
            <div className={styles.emptyState}><CheckCircle size={40} color="#10b981" /><p>No unmapped hotels for these filters</p></div>
          ) : (
            <div className={styles.listRows}>
              {listRows.map((row) => (
                <div key={row.id} className={styles.listRowCard}>
                  <div className={styles.listRowMain}>
                    <div className={styles.listRowInfo}>
                      <div className={styles.listRowName}>{row.displayName || row.name}</div>
                      <div className={styles.listRowMeta}>
                        <MapPin size={11} />
                        <span>{row.city || "—"}{row.countryCode ? `, ${row.countryCode}` : ""}</span>
                        {row.zoneName && <span className={styles.zoneTag}>{row.zoneName}</span>}
                        <span className={styles.codeTag}>#{row.hotelCode}</span>
                      </div>
                    </div>
                    <button className={styles.mapNowBtn} onClick={() => openInMapper(row)} title="Map this hotel now">
                      <Link2 size={13} /> Map This Hotel
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {listPagination && listPagination.totalPages > 1 && (
            <div className={styles.pagination}>
              <button className={styles.prevButton} onClick={() => setListPage(listPage - 1)} disabled={listPage === 1}>Prev</button>
              <span className={styles.pageNumber}>{listPage} / {listPagination.totalPages}</span>
              <button className={styles.nextButton} onClick={() => setListPage(listPage + 1)} disabled={listPage >= listPagination.totalPages}>Next</button>
            </div>
          )}
        </div>
      )}

      {/* ═══════════ MAPPER VIEW (3 panels) ═══════════ */}
      {view === "map" && (
      <div className={styles.mainContent}>

        {/* LEFT: Master Hotel Selection */}
        <div className={styles.hotelbedsSection}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>
              <Building2 size={20} />
              <span>Select Master Hotel</span>
            </div>
            <div className={styles.sectionCount}>{filteredHotelbeds.length}</div>
          </div>
          <div className={styles.searchBox}>
            <Search size={16} />
            <input
              type="text"
              placeholder="Search hotels..."
              value={hotelbedsSearch}
              onChange={(e) => setHotelbedsSearch(e.target.value)}
              className={styles.searchInput}
            />
            {hotelbedsSearch && (
              <button
                className={styles.searchClear}
                onClick={() => { setHotelbedsSearch(""); setSelectedHotelbeds(null); setSelectedSuppliers([]); }}
                title="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <div className={styles.hotelList} onScroll={handleScroll}>
            {hotelsLoading ? (
              <div className={styles.loadingState}>
                <Loader2 size={24} className={styles.spinner} />
                <p>Loading hotels...</p>
              </div>
            ) : hotelsError ? (
              <div className={styles.errorState}>
                <AlertCircle size={24} color="#ef4444" />
                <p>Failed to load hotels: {hotelsError}</p>
              </div>
            ) : filteredHotelbeds.length === 0 ? (
              <div className={styles.emptyState}>
                <Building2 size={40} color="#cbd5e1" />
                <p>No hotels found</p>
              </div>
            ) : (
              <>
                {filteredHotelbeds.map((hotel, index) => (
                  <div
                    key={`${hotel.id}-${index}`}
                    className={`${styles.hotelCard} ${selectedHotelbeds?.id === hotel.id ? styles.selectedHotelCard : ''}`}
                    onClick={() => handleSelectHotelbeds(hotel)}
                  >
                    <div className={styles.hotelIconWrapper}>
                      <Building2 size={18} />
                    </div>
                    <div className={styles.hotelDetails}>
                      <div className={styles.hotelName}>{hotel.name}</div>
                      <div className={styles.hotelMeta}>
                        <div className={styles.stars}>
                          {Array.from({ length: hotel.stars }).map((_, i) => (
                            <Star key={i} size={10} fill="#f59e0b" color="#f59e0b" />
                          ))}
                        </div>
                        <span className={styles.hotelLocation}>
                          <MapPin size={10} />
                          {hotel.city}
                        </span>
                      </div>
                      <div className={styles.hotelZone}>{hotel.zone}</div>
                    </div>
                    {selectedHotelbeds?.id === hotel.id && (
                      <ChevronRight size={20} className={styles.selectedIndicator} />
                    )}
                  </div>
                ))}
                {hasMoreHotels && filteredHotelbeds.length > 0 && (
                  <div className={styles.loadMoreContainer}>
                    <button
                      className={styles.loadMoreButton}
                      onClick={loadMoreHotels}
                      disabled={loadingMoreHotels}
                    >
                      {loadingMoreHotels ? (
                        <><Loader2 size={16} className={styles.spinner} />Loading...</>
                      ) : (
                        <><Loader2 size={16} />Load More Hotels</>
                      )}
                    </button>
                  </div>
                )}
                {!hasMoreHotels && filteredHotelbeds.length > 0 && (
                  <div className={styles.endOfList}><p>End of hotels list</p></div>
                )}
              </>
            )}
          </div>
        </div>

        {/* CENTER: Mapping Panel */}
        {selectedHotelbeds ? (
          <div className={styles.mappingCenter} ref={mappingCenterRef}>
            <div className={styles.selectedHotelDisplay}>
              <div className={styles.masterHotelBadgeRow}>
                <div className={styles.masterHotelBadge}>Master Hotel</div>
                <button
                  className={styles.deselectHotel}
                  onClick={() => { setSelectedHotelbeds(null); setSelectedSuppliers([]); setSupplierSuggestions([]); }}
                  title="Deselect hotel"
                >
                  <X size={14} /> Clear
                </button>
              </div>
              <div className={styles.masterHotelCard}>
                <Building2 size={24} color="#f59e0b" />
                <div className={styles.masterHotelInfo}>
                  <div className={styles.masterHotelName}>{selectedHotelbeds.name}</div>
                  <div className={styles.masterHotelLocation}>
                    <MapPin size={12} />
                    {selectedHotelbeds.city}{selectedHotelbeds.country ? `, ${selectedHotelbeds.country}` : ""}
                  </div>
                </div>
              </div>

              {/* Already Mapped Suppliers */}
              {mappingsLoading ? (
                <div className={styles.loadingCenter}>
                  <Loader2 size={24} className={styles.spinner} />
                  <p>Loading mappings...</p>
                </div>
              ) : mappedSuppliers.length > 0 ? (
                <div className={styles.mappedSection}>
                  <div className={styles.mappedHeader}>
                    <CheckCircle size={14} />
                    <span>Mapped from {mappedSuppliers.length} Supplier(s)</span>
                  </div>
                  <div className={styles.mappedList}>
                    {mappedSuppliers.map((supplier) => (
                      <div key={supplier.id} className={styles.mappedItem}>
                        <div className={styles.mappedItemInfo}>
                          <Link2 size={12} color="#10b981" />
                          <span className={styles.mappedItemName}>{supplier.supplierHotelName}</span>
                          <span className={styles.mappedItemSupplier}>{supplier.supplierFullName}</span>
                        </div>
                        <button
                          className={styles.unmapButton}
                          onClick={() => handleUnmap(supplier.id, supplier.supplierHotelName)}
                          disabled={deleteLoading}
                        >
                          {deleteLoading ? <Loader2 size={12} className={styles.spinner} /> : <Unlink size={12} />}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className={styles.emptyMapped}>
                  <Link2 size={32} />
                  <p>No suppliers mapped yet</p>
                  <span>Select suppliers from the right panel and click Map</span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className={styles.emptyCenter} ref={mappingCenterRef}>
            <ArrowRight size={48} color="#cbd5e1" />
            <p>Select a master hotel to start mapping</p>
          </div>
        )}

        {/* RIGHT: Matching Suppliers */}
        <div className={styles.suppliersSection}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>
              <Link2 size={20} />
              <span>Matching Suppliers</span>
            </div>
            <div className={styles.sectionCount}>
              {availableSuppliers.length} total
              {loadingSuggestions && ' (Loading...)'}
            </div>
          </div>
          <div className={styles.searchBox}>
            <Search size={16} />
            <input
              type="text"
              placeholder="Search suppliers..."
              value={supplierSearch}
              onChange={(e) => setSupplierSearch(e.target.value)}
              className={styles.searchInput}
              disabled={!selectedHotelbeds}
            />
          </div>

          {supplierErrors.length > 0 && (
            <div className={styles.supplierErrorBar}>
              <AlertCircle size={13} />
              <span>
                {supplierErrors.map((e) => `${e.supplierName}: ${e.error}`).join(" · ")}
              </span>
            </div>
          )}

          {selectedHotelbeds && selectedSuppliers.length > 0 && (
            <div className={styles.actionBar}>
              <span className={styles.selectedCount}>{selectedSuppliers.length} selected</span>
              <button
                className={styles.mapButton}
                onClick={handleMapSelected}
                disabled={createLoading || selectedSuppliers.length === 0}
              >
                {createLoading ? <Loader2 size={14} className={styles.spinner} /> : <Link2 size={14} />}
                {createLoading ? 'Mapping...' : 'Map Selected'}
              </button>
            </div>
          )}

          <div className={styles.supplierList}>
            {!selectedHotelbeds ? (
              <div className={styles.emptyState}>
                <Building2 size={40} color="#cbd5e1" />
                <p>Select a master hotel first</p>
              </div>
            ) : loadingSuggestions ? (
              <div className={styles.loadingState}>
                <Loader2 size={24} className={styles.spinner} />
                <p>Fetching supplier hotels...</p>
                <span className={styles.loadingHint}>First fetch from a supplier can take up to a minute — after that it's cached.</span>
              </div>
            ) : availableSuppliers.length === 0 ? (
              <div className={styles.emptyState}>
                <CheckCircle size={40} color="#10b981" />
                <p>No matching supplier hotels found</p>
              </div>
            ) : (
              <>
                <div className={styles.supplierHint}>
                  <Globe size={14} />
                  <span>
                    {supplierFilter === "all" ? "All suppliers" : supplierNameOf(supplierFilter) || "Supplier"} · suggestions for {selectedHotelbeds.name.slice(0, 24)}{selectedHotelbeds.name.length > 24 ? '…' : ''}
                  </span>
                </div>
                {paginatedSuppliers.map((supplier) => {
                  const isSelected = selectedSuppliers.find((s) => s.id === supplier.id);
                  const isMappedHere = supplier.mappedToThisHotel;
                  const isMappedElsewhere = !isMappedHere && supplier.mappedToHotelbedsId;
                  return (
                    <div
                      key={supplier.id}
                      className={`${styles.supplierCard} ${isSelected ? styles.selectedSupplierCard : ''} ${isMappedHere ? styles.mappedSupplierCard : ''}`}
                      onClick={() => handleToggleSupplier(supplier)}
                    >
                      <div className={styles.supplierIconWrapper}>
                        {isMappedHere ? <CheckCircle size={16} color="#10b981" /> : <Link2 size={16} />}
                      </div>
                      <div className={styles.supplierDetails}>
                        <div className={styles.supplierName}>{supplier.name}</div>
                        <div className={styles.supplierMeta}>
                          <span className={styles.supplierBadge}>{supplier.supplier}</span>
                          <span className={styles.supplierCode}>{supplier.supplierCode}</span>
                          {isMappedHere && <span className={styles.mappedTag}>Mapped</span>}
                          {isMappedElsewhere && (
                            <span className={styles.mappedElsewhereTag} title={`Already mapped to ${supplier.mappedToHotelbedsName || 'another hotel'}`}>
                              Mapped elsewhere
                            </span>
                          )}
                        </div>
                        <div className={styles.supplierLocation}>
                          <MapPin size={10} />
                          {[supplier.city, supplier.province]
                            .filter((v, i, a) => v && a.indexOf(v) === i)
                            .join(', ')}
                        </div>
                        {/* The supplier's own street address, shown for the same
                            reason the master card shows one: two hotels in the
                            same resort often differ only by street, and that is
                            exactly the call the mapper has to make. */}
                        {supplier.address && (
                          <div className={styles.supplierAddress} title={supplier.address}>
                            {supplier.address}
                          </div>
                        )}
                      </div>
                      <div
                        className={styles.matchScore}
                        style={{
                          background: getConfidenceColor(supplier.matchScore) + '20',
                          color: getConfidenceColor(supplier.matchScore)
                        }}
                      >
                        {Math.round(supplier.matchScore)}%
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
          {totalSupplierPages > 1 && (
            <div className={styles.pagination}>
              <button
                className={styles.prevButton}
                onClick={() => setSupplierPage(supplierPage - 1)}
                disabled={supplierPage === 1}
              >
                Prev
              </button>
              <span className={styles.pageNumber}>{supplierPage} / {totalSupplierPages}</span>
              <button
                className={styles.nextButton}
                onClick={() => setSupplierPage(supplierPage + 1)}
                disabled={supplierPage === totalSupplierPages}
              >
                Next
              </button>
            </div>
          )}
        </div>

      </div>
      )}
    </div>
  );
};

export default HotelMappingTab;
